import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * modules module ServiceStores (the Module 360 feature area).
 *
 * Declares ONLY the Feathers services this area introduces. ViewStores read
 * `rootStore.<name>Store` and call `.find/.get/.create/.patch/.remove`.
 *
 * DELIBERATELY NOT REDECLARED — these already exist and the mapped
 * `rootStore.<name>Store` is shared:
 *   - `module-catalog`  → clients.stores.ts (the assignment picker's vocabulary)
 *   - `client-modules`  → clients.stores.ts
 *   - `communications` / `documents` / `devops-events` → clients.stores.ts
 *   - `devops-links`    → devops.stores.ts
 *   - `audit-log`       → admin.stores.ts
 *   - `users`           → user.stores.ts
 * Redeclaring a service path is DROPPED under "first registration wins" (and
 * becomes a hard error once `onDuplicateStore: 'throw'` is set), so a duplicate
 * here would not even be the thing that takes effect.
 *
 * idField: "id" for the standard CRUD/read tables. The find-only derived
 * services have no `id` column — `module-summary` is one row per catalog entry
 * keyed by `governedSlug`, `module-adoption` is one row per client, and
 * `module-activity` items carry the merged timeline `key`. Their ViewStores
 * read the `find()` RESPONSE rows (re-queried per load), not the entity cache.
 *
 * enableOffline OFF where the offline wrapper's blanket sync-on-reconnect (a
 * bare `find()` with NO query) cannot succeed — the same reasoning documented
 * in devops.stores.ts and system.stores.ts, and asserted by
 * `serviceStores/enableOffline.contract.test.ts`:
 *   - `module-adoption` and `module-activity` REQUIRE a `governedSlug` query
 *     param and throw BadRequest without one, so a bare find can only log an
 *     error and drop the store into offline fallback on every socket connect.
 *   - `module-adoption` additionally resolves a composite `{ rows, totals,
 *     truncated }` object rather than a `{ data, total }` envelope, which the
 *     list-oriented OfflineServiceStore cannot represent (it would null the
 *     result).
 * `module-summary` keeps the default (offline-capable): it takes no required
 * param and returns one row per catalog entry, exactly like `client-summary`.
 */
export const modulesStores: ServiceStoreConfig[] = [
  // Module ownership (the module-side `client_contacts`) — standard CRUD.
  { name: "module-owners", options: { idField: "id", enableStorage: false } },
  // Sync-owned governed facts + observed releases. Read-only through the API
  // (`internalOnly()` on create/patch/remove); find/get only from the web.
  //
  // Both carry `serviceView` — the auto-generated raw grid under
  // /management/data/<path>, in the Management nav group (see
  // views/utils/ServiceViewRouteGenerator.ts). They are the safest possible
  // candidates for it: the API refuses create/patch/remove from ANY external
  // caller, so the generated page is read-only no matter what the UI offers,
  // and Module 360 only ever shows these one module at a time — a fleet-wide
  // "what did the last `yarn catalog:sync` actually write" grid does not exist
  // anywhere else. `columnHeaders` are declared explicitly rather than inferred
  // from row 0 so the column set is a deliberate choice (the JSON `detail` /
  // `evidence` blobs stay out of the grid) and does not shift with the data.
  // No `actions` block ⇒ no inline edit, no delete column.
  {
    name: "module-governance",
    options: {
      idField: "id",
      enableStorage: false,
      serviceView: {
        enableServiceView: true,
        columnHeaders: [
          { key: "governedSlug", title: "Governed Slug", dataType: "string", width: 180 },
          { key: "factType", title: "Fact Type", dataType: "string", width: 120 },
          { key: "factKey", title: "Fact Key", dataType: "string", width: 200 },
          { key: "label", title: "Label", dataType: "string", width: 260 },
          { key: "status", title: "Status", dataType: "string", width: 130 },
          { key: "severity", title: "Severity", dataType: "string", width: 110 },
          { key: "sourceUpdated", title: "Source Updated", dataType: "string", width: 140 },
          { key: "sortOrder", title: "Sort Order", dataType: "number", width: 110 },
        ],
        defaultSort: {
          sortBy: ["governedSlug"],
          sortDirection: { governedSlug: "ASC" },
        },
      },
    },
  },
  {
    name: "module-releases",
    options: {
      idField: "id",
      enableStorage: false,
      serviceView: {
        enableServiceView: true,
        columnHeaders: [
          { key: "governedSlug", title: "Governed Slug", dataType: "string", width: 180 },
          { key: "packageName", title: "Package", dataType: "string", width: 280 },
          { key: "version", title: "Version", dataType: "string", width: 120 },
          { key: "state", title: "State", dataType: "string", width: 140 },
          { key: "source", title: "Source", dataType: "string", width: 150 },
          { key: "observedAt", title: "Observed At", dataType: "date", width: 160 },
          { key: "syncedAt", title: "Synced At", dataType: "date", width: 160 },
        ],
        defaultSort: {
          sortBy: ["observedAt"],
          sortDirection: { observedAt: "DESC" },
        },
      },
    },
  },
  // Find-only rollup — one row per catalog entry, keyed by governedSlug.
  { name: "module-summary", options: { idField: "governedSlug", enableStorage: false } },
  // Find-only derived feeds — both require a `governedSlug` query param.
  {
    name: "module-activity",
    options: { idField: "key", enableStorage: false, enableOffline: false },
  },
  {
    name: "module-adoption",
    options: { idField: "clientId", enableStorage: false, enableOffline: false },
  },
];
